/**
 * Route guard for the authenticated console.
 *
 * - Not authenticated  -> redirect to /login (remembering where they were).
 * - Missing a required permission -> redirect to /dashboard.
 *
 * Business-existence is intentionally NOT enforced here; the onboarding
 * screens own that flow (search/add business) and redirect themselves.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, can, isAdmin, isAdministrator, PermissionKey } from './session';

interface ProtectedRouteProps {
  children: React.ReactElement;
  /** Optional permission flag the user must have. */
  permission?: PermissionKey;
  /** Require administrator/admin role. */
  adminOnly?: boolean;
  /** Require Administrator (business creator) — billing & business edit. */
  administratorOnly?: boolean;
  /** Where to send a user who fails the guard (default /dashboard). */
  redirect?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, permission, adminOnly, administratorOnly, redirect }) => {
  const location = useLocation();
  const fallback = redirect || '/dashboard';

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (administratorOnly && !isAdministrator()) {
    return <Navigate to={fallback} replace />;
  }
  if (adminOnly && !isAdmin()) {
    return <Navigate to={fallback} replace />;
  }
  if (permission && !can(permission)) {
    return <Navigate to={fallback} replace />;
  }
  return children;
};

export default ProtectedRoute;
